<script setup lang="ts">
import { cloneDeep } from 'lodash'
import { computed, ref, watch } from 'vue'
import type {
  SummaryGroupingContext,
  SummaryGroupingDraft,
  SummaryGroupingDraftInput,
  SummaryGroupingSettings,
} from '../model/summaryGroupingDraft'
import {
  buildSummaryGroupingDraft,
  summaryGroupingDraftEquals,
  toPersistedSummaryGroupingSettings,
} from '../model/summaryGroupingDraft'
import { ComputedConditionList, ComputedTypeList } from '@/entities/Inventory/TypeField'
import { VIcons, VSelectSearch } from '@/shared/ui'

type SummaryGroupingOption = {
  title: string
  value: string | number | null
}

const SINGLE_VALUE_CONDITIONS = new Set(['MORE', 'LESS', 'EQUAL', 'INCLUDE'])
const SUPPORTED_CONDITIONS = new Set([...SINGLE_VALUE_CONDITIONS, 'RANGE', 'IN_LIST'])

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    context: SummaryGroupingContext
    savedSettings: SummaryGroupingDraftInput
    sameListFieldItems?: SummaryGroupingOption[]
    inListItems?: SummaryGroupingOption[]
    groupingTypeItems?: SummaryGroupingOption[]
    conditionItems?: SummaryGroupingOption[]
    filterSourceItems?: SummaryGroupingOption[]
    canSwitchPrev?: boolean
    canSwitchNext?: boolean
  }>(),
  {
    title: '',
    sameListFieldItems: () => [],
    inListItems: () => [],
    groupingTypeItems: () =>
      ComputedTypeList.map((item) => ({
        title: item.title,
        value: item.value,
      })),
    conditionItems: () =>
      ComputedConditionList.map((item) => ({
        title: item.title,
        value: item.value,
      })),
    filterSourceItems: () => [
      { title: 'Текущее поле', value: 'current' },
      { title: 'Другое поле', value: 'another-field' },
    ],
    canSwitchPrev: true,
    canSwitchNext: true,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  save: [value: SummaryGroupingSettings]
  switch: [direction: 'prev' | 'next']
}>()

const savedSnapshot = ref<SummaryGroupingDraft | null>(null)
const draft = ref<SummaryGroupingDraft | null>(null)

function syncFromProps() {
  const next = buildSummaryGroupingDraft(props.savedSettings ?? {}, props.context)
  if (
    String(next.computed_condition_id) === 'IN_LIST' &&
    !Array.isArray(next.condition_value_in_list)
  ) {
    next.condition_value_in_list = []
  }
  savedSnapshot.value = next
  draft.value = cloneDeep(next)
}

watch(
  () =>
    [
      props.modelValue,
      props.savedSettings,
      props.context.current_field_id,
      props.context.list_id,
    ] as const,
  ([open]) => {
    if (open) syncFromProps()
  },
  { deep: true, immediate: true },
)

const modalTitle = computed(() => props.title?.trim() || 'Сводная информация')

const sameListFieldOptions = computed(() =>
  (props.sameListFieldItems ?? []).filter((item) => {
    if (item.value == null) return false
    return String(item.value) !== String(props.context.current_field_id)
  }),
)

const sameListFieldOptionValues = computed(
  () => new Set(sameListFieldOptions.value.map((item) => String(item.value))),
)

const hasLoadedSameListFieldItems = computed(() => (props.sameListFieldItems ?? []).length > 0)

/** Unified «Поле» value: sentinel = filter on current field; otherwise same-list field id. */
const FILTER_FIELD_CURRENT = '__pb_summary_filter_current__'

const filterFieldItems = computed<SummaryGroupingOption[]>(() => {
  const currentOpt =
    (props.filterSourceItems ?? []).find((x) => String(x.value) === 'current') ?? {
      title: 'Текущее поле',
      value: 'current',
    }
  return [{ title: String(currentOpt.title), value: FILTER_FIELD_CURRENT }, ...sameListFieldOptions.value]
})

const filterFieldModelValue = computed(() => {
  if (!draft.value) return null
  if (draft.value.filter_source === 'current') return FILTER_FIELD_CURRENT
  return draft.value.optional_field_id
})

function onUnifiedFilterFieldChange(value: string | number | null) {
  ensureDraft()
  if (!draft.value) return
  if (value === FILTER_FIELD_CURRENT || value === null || value === '') {
    draft.value.filter_source = 'current'
    draft.value.optional_field_id = null
    return
  }
  draft.value.filter_source = 'another-field'
  draft.value.optional_field_id = value
}

const isDirty = computed(() => {
  if (!draft.value || !savedSnapshot.value) return false
  return !summaryGroupingDraftEquals(draft.value, savedSnapshot.value, props.context)
})

const canSwitchLeft = computed(() => Boolean(props.canSwitchPrev) && !isDirty.value)
const canSwitchRight = computed(() => Boolean(props.canSwitchNext) && !isDirty.value)

/** Shown when prev/next are disabled because the draft was edited (navigation stays data-safe). */
const switchNavBlockedTitle = computed(() =>
  isDirty.value ? 'Сохраните или сбросьте изменения, чтобы переключить поле' : undefined,
)

const singleValueCondition = computed(() => {
  const condition = draft.value?.computed_condition_id
  return condition != null && SINGLE_VALUE_CONDITIONS.has(String(condition))
})

const showValueInputs = computed(() => isSupportedCondition(draft.value?.computed_condition_id))
const showRangeInputs = computed(() => String(draft.value?.computed_condition_id) === 'RANGE')
const showListInputs = computed(() => String(draft.value?.computed_condition_id) === 'IN_LIST')

const canSave = computed(() => {
  const current = draft.value
  if (!current?.computed_type_id) return false

  if (
    current.filter_source === 'another-field' &&
    !isValidOptionalFieldId(current.optional_field_id)
  ) {
    return false
  }
  if (current.computed_condition_id == null) return true
  if (!isSupportedCondition(current.computed_condition_id)) return false

  if (singleValueCondition.value) {
    return hasScalarValue(current.condition_value)
  }

  if (showRangeInputs.value) {
    return (
      hasScalarValue(current.condition_value_range.min) &&
      hasScalarValue(current.condition_value_range.max) &&
      !isInvertedRange(current.condition_value_range.min, current.condition_value_range.max)
    )
  }

  if (showListInputs.value) {
    return (
      Array.isArray(current.condition_value_in_list) && current.condition_value_in_list.length > 0
    )
  }

  return true
})

function hasScalarValue(value: string | number | null | undefined) {
  return value !== null && value !== undefined && value !== ''
}

function isSupportedCondition(value: string | number | null | undefined) {
  return value != null && SUPPORTED_CONDITIONS.has(String(value))
}

function isValidOptionalFieldId(value: string | number | null) {
  if (!hasLoadedSameListFieldItems.value) return true
  return value != null && sameListFieldOptionValues.value.has(String(value))
}

function toComparableNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null
  const normalized = typeof value === 'number' ? value : Number(String(value).trim())
  return Number.isFinite(normalized) ? normalized : null
}

function isInvertedRange(
  min: string | number | null | undefined,
  max: string | number | null | undefined,
) {
  const minValue = toComparableNumber(min)
  const maxValue = toComparableNumber(max)
  return minValue != null && maxValue != null && minValue > maxValue
}

function ensureDraft() {
  if (!draft.value) syncFromProps()
}

function clearAllConditionValues() {
  if (!draft.value) return
  draft.value.condition_value = null
  draft.value.condition_value_range = { min: null, max: null }
  draft.value.condition_value_in_list = null
}

function clearSingleAndListValues() {
  if (!draft.value) return
  draft.value.condition_value = null
  draft.value.condition_value_in_list = null
}

function clearRangeAndListValues() {
  if (!draft.value) return
  draft.value.condition_value_range = { min: null, max: null }
  draft.value.condition_value_in_list = null
}

function clearSingleAndRangeValues() {
  if (!draft.value) return
  draft.value.condition_value = null
  draft.value.condition_value_range = { min: null, max: null }
}

function onConditionChange(value: string | number | null) {
  ensureDraft()
  if (!draft.value) return

  draft.value.computed_condition_id = value === null || value === '' ? null : String(value)
  if (draft.value.computed_condition_id == null) {
    clearAllConditionValues()
    return
  }

  if (SINGLE_VALUE_CONDITIONS.has(String(draft.value.computed_condition_id))) {
    clearRangeAndListValues()
    return
  }

  if (String(draft.value.computed_condition_id) === 'RANGE') {
    clearSingleAndListValues()
    return
  }

  if (String(draft.value.computed_condition_id) === 'IN_LIST') {
    clearSingleAndRangeValues()
  }
}

function onSingleValueChange(value: string | number | null) {
  ensureDraft()
  if (!draft.value) return
  draft.value.condition_value = value === '' || value == null ? null : value
}

function onRangeMinChange(value: string | number | null) {
  ensureDraft()
  if (!draft.value) return
  draft.value.condition_value_range.min = value === '' || value == null ? null : value
}

function onRangeMaxChange(value: string | number | null) {
  ensureDraft()
  if (!draft.value) return
  draft.value.condition_value_range.max = value === '' || value == null ? null : value
}

function onListChange(value: Array<string | number> | null) {
  ensureDraft()
  if (!draft.value) return
  draft.value.condition_value_in_list = Array.isArray(value) && value.length > 0 ? value : []
}

function resetDraft() {
  if (!savedSnapshot.value) return
  draft.value = cloneDeep(savedSnapshot.value)
  if (
    draft.value &&
    String(draft.value.computed_condition_id) === 'IN_LIST' &&
    !Array.isArray(draft.value.condition_value_in_list)
  ) {
    draft.value.condition_value_in_list = []
  }
}

function closeDialog() {
  emit('close')
  emit('update:modelValue', false)
}

function onDialogUpdate(value: boolean) {
  if (value) {
    emit('update:modelValue', true)
    return
  }
  closeDialog()
}

function saveDraft() {
  if (!draft.value || !canSave.value) return
  emit('save', toPersistedSummaryGroupingSettings(cloneDeep(draft.value), props.context))
}

function switchField(direction: 'prev' | 'next') {
  if (isDirty.value) return
  if (direction === 'prev' && !props.canSwitchPrev) return
  if (direction === 'next' && !props.canSwitchNext) return
  emit('switch', direction)
}

function onSwitchClick(direction: 'prev' | 'next', event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  switchField(direction)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    attach="body"
    :z-index="3400"
    max-width="548"
    scrollable
    content-class="pb-summary-grouping-modal__dialog"
    :retain-focus="false"
    @update:model-value="onDialogUpdate"
  >
    <VCard class="pb-summary-grouping-modal__card" rounded="xl">
      <VToolbar
        density="compact"
        color="surface"
        flat
        class="pb-summary-grouping-modal__toolbar px-4"
      >
        <VBtn
          type="button"
          icon
          variant="text"
          density="compact"
          class="pb-summary-grouping-modal__nav-btn"
          :disabled="!canSwitchLeft"
          :title="!canSwitchLeft ? switchNavBlockedTitle : undefined"
          aria-label="Предыдущее поле"
          @click="onSwitchClick('prev', $event)"
        >
          <VIcons type="chevron_left" />
        </VBtn>

        <VToolbarTitle class="pb-summary-grouping-modal__toolbar-title text-body-1 font-weight-bold">
          {{ modalTitle }}
        </VToolbarTitle>

        <VBtn
          type="button"
          icon
          variant="text"
          density="compact"
          class="pb-summary-grouping-modal__nav-btn"
          :disabled="!canSwitchRight"
          :title="!canSwitchRight ? switchNavBlockedTitle : undefined"
          aria-label="Следующее поле"
          @click="onSwitchClick('next', $event)"
        >
          <VIcons type="chevron_right" />
        </VBtn>

        <VBtn
          type="button"
          icon
          variant="text"
          density="compact"
          class="pb-summary-grouping-modal__nav-btn pb-summary-grouping-modal__close-btn"
          aria-label="Закрыть"
          @click="closeDialog"
        >
          <VIcons type="close" />
        </VBtn>
      </VToolbar>

      <VCardText v-if="draft" class="pb-summary-grouping-modal__body">
        <div class="pb-summary-grouping-modal__row">
          <div class="pb-summary-grouping-modal__label">Тип группировки</div>
          <VSelect
            :model-value="draft.computed_type_id"
            :items="groupingTypeItems"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="draft.computed_type_id = $event"
          />
        </div>

        <fieldset class="pb-summary-grouping-modal__filter-group">
          <legend class="pb-summary-grouping-modal__filter-legend text-caption text-medium-emphasis px-1">
            {{ $t('itf.filter') }}
          </legend>
          <div class="pb-summary-grouping-modal__filter-row">
            <VSelectSearch
              :model-value="filterFieldModelValue"
              :items="filterFieldItems"
              item-title="title"
              item-value="value"
              :label="$t('itf.filter_field')"
              density="compact"
              variant="outlined"
              hide-details
              :min-search-length="0"
              class="pb-summary-grouping-modal__filter-control"
              @update:model-value="onUnifiedFilterFieldChange"
            />
            <VSelect
              :model-value="draft.computed_condition_id"
              :items="conditionItems"
              item-title="title"
              item-value="value"
              :label="$t('itf.filter_condition')"
              density="compact"
              variant="outlined"
              hide-details
              class="pb-summary-grouping-modal__filter-control"
              @update:model-value="onConditionChange"
            />
          </div>
        </fieldset>

        <div v-if="showValueInputs" class="pb-summary-grouping-modal__row">
          <div class="pb-summary-grouping-modal__label">Значение</div>

          <template v-if="singleValueCondition">
            <VTextField
              :model-value="draft.condition_value"
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="onSingleValueChange"
            />
          </template>

          <template v-else-if="showRangeInputs">
            <div class="pb-summary-grouping-modal__range">
              <VTextField
                :model-value="draft.condition_value_range.min"
                label="От"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="onRangeMinChange"
              />
              <VTextField
                :model-value="draft.condition_value_range.max"
                label="До"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="onRangeMaxChange"
              />
            </div>
          </template>

          <template v-else-if="showListInputs">
            <VSelectSearch
              :model-value="draft.condition_value_in_list"
              :items="inListItems"
              item-title="title"
              item-value="value"
              multiple
              short
              density="compact"
              variant="outlined"
              hide-details
              :min-search-length="0"
              @update:model-value="onListChange"
            />
          </template>
        </div>
      </VCardText>

      <VCardActions class="pb-summary-grouping-modal__footer">
        <VSpacer />
        <VBtn color="primary" variant="flat" :disabled="!canSave" @click="saveDraft">
          {{ $t('itf.save') }}
        </VBtn>
        <VBtn variant="tonal" :disabled="!isDirty" @click="resetDraft">
          {{ $t('itf.reset') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.pb-summary-grouping-modal__card {
  overflow: hidden;
  border-radius: 16px;
}

.pb-summary-grouping-modal__toolbar {
  border-radius: 16px 16px 0 0;
}

.pb-summary-grouping-modal__toolbar-title {
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pb-summary-grouping-modal__nav-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  min-width: 36px !important;
  padding: 0 !important;
  border-radius: 50% !important;
}

.pb-summary-grouping-modal__close-btn {
  margin-inline-start: 4px;
}

.pb-summary-grouping-modal__body {
  display: grid;
  gap: 12px;
  padding: 16px !important;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.pb-summary-grouping-modal__filter-group {
  margin: 0;
  min-width: 0;
  padding: 10px 12px 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.23);
  border-radius: 4px;
}

.pb-summary-grouping-modal__filter-legend {
  float: unset;
  width: max-content;
  max-width: 100%;
  padding: 0 4px;
  margin-bottom: 8px;
  line-height: 1.2;
}

.pb-summary-grouping-modal__filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.pb-summary-grouping-modal__filter-control {
  min-width: 0;
}

.pb-summary-grouping-modal__row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.pb-summary-grouping-modal__label {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  color: rgba(23, 32, 51, 0.82);
}

.pb-summary-grouping-modal__range {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.pb-summary-grouping-modal__footer {
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

@media (max-width: 640px) {
  .pb-summary-grouping-modal__body {
    padding-inline: 12px !important;
  }

  .pb-summary-grouping-modal__filter-row {
    grid-template-columns: 1fr;
  }

  .pb-summary-grouping-modal__row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .pb-summary-grouping-modal__label {
    padding-inline: 2px;
  }

  .pb-summary-grouping-modal__range {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.pb-summary-grouping-modal__dialog {
  padding: 16px;
  box-sizing: border-box;
  width: min(548px, 100%);
}
</style>
